// Wishlist.js
import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/apiConfig';

const SESSION_WISHLIST_KEY = 'ccc_guest_wishlist';
const SESSION_CART_KEY = 'ccc_guest_cart';
const SESSION_SIZE_KEY = 'ccc_guest_size';

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const parseAccessorySelections = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getGuestWishlist = () => safeParse(sessionStorage.getItem(SESSION_WISHLIST_KEY), []);
const setGuestWishlist = (items) => {
  try {
    sessionStorage.setItem(SESSION_WISHLIST_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_wishlist_updated'));
};

const getGuestCart = () => safeParse(sessionStorage.getItem(SESSION_CART_KEY), []);
const setGuestCart = (items) => {
  try {
    sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_cart_updated'));
};

const getGuestSize = () => safeParse(sessionStorage.getItem(SESSION_SIZE_KEY), null);

const Wishlist = () => {
  const navigate = useNavigate();

  const [wishlist, setWishlist] = useState([]);
  const [user, setUser] = useState(() => safeParse(localStorage.getItem('user'), null));
  const [guestSize, setGuestSizeState] = useState(() => getGuestSize());

  const token = localStorage.getItem('token');
  const isLoggedIn = !!user?.userId && !!token;

  const normalizeWishlist = (arr) => {
    const items = Array.isArray(arr) ? arr : [];

    return items.map((x) => {
      // normalize id
      const wishlistId = x.wishlistId ?? x.wishlistItemId ?? x.id ?? null;
      const guestWishlistId = x.guestWishlistId ?? x.id ?? null;

      // normalize image
      const fabricImageUrl =
        x.fabricImageUrl ??
        x.fabricImageURL ??
        x.fabricImage ??
        x.fabricImagePath ??
        x.firstFabricImage ??
        x.imageUrl ??
        x.imageURL ??
        null;

      // normalize names
      const productName = x.productName ?? x.ProductName ?? '—';
      const fabricName = x.fabricName ?? x.FabricName ?? '';

      // normalize styles
      const styleOptionNames =
        Array.isArray(x.styleOptionNames) ? x.styleOptionNames :
        Array.isArray(x.styleOptions) ? x.styleOptions :
        typeof x.styleOptions === 'string'
          ? x.styleOptions.split(',').map(s => s.trim()).filter(Boolean)
          : [];
      const accessorySelections = parseAccessorySelections(
        x.accessorySelectionsJson ?? x.accessorySelections ?? x.AccessorySelectionsJson ?? null
      );

      const price = Number(x.price ?? x.totalPrice ?? x.total_price ?? 0);

      // keep a stable row key
      const _id =
        wishlistId ??
        guestWishlistId ??
        `${x.productId || x.productID || 'p'}-${Date.now()}-${Math.random()}`;

      return {
        ...x,
        wishlistId,
        guestWishlistId,
        fabricImageUrl,
        productName,
        fabricName,
        styleOptionNames,
        accessorySelections,
        price,
        _id
      };
    });
  };

  const loadWishlist = async () => {
    if (isLoggedIn) {
      try {
        const res = await fetch(`${API_BASE_URL}/UserManagement/GetWishlistByUserId/${user.userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('GetWishlistByUserId failed:', res.status, text);
          setWishlist([]);
          return;
        }

        const data = await res.json();
        setWishlist(normalizeWishlist(data));
      } catch (err) {
        console.error('Failed to load wishlist:', err);
        setWishlist([]);
      }
      return;
    }

    // guest
    setGuestSizeState(getGuestSize());
    setWishlist(normalizeWishlist(getGuestWishlist()));
  };

  useEffect(() => {
    const syncUser = () => setUser(safeParse(localStorage.getItem('user'), null));
    const syncWishlist = () => loadWishlist();
    const syncGuestSize = () => setGuestSizeState(getGuestSize());

    window.addEventListener('storage', syncUser);
    window.addEventListener('ccc_user_updated', syncUser);

    window.addEventListener('ccc_wishlist_updated', syncWishlist);
    window.addEventListener('ccc_guest_size_updated', syncGuestSize);

    loadWishlist();

    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('ccc_user_updated', syncUser);
      window.removeEventListener('ccc_wishlist_updated', syncWishlist);
      window.removeEventListener('ccc_guest_size_updated', syncGuestSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user?.userId]);

  const pickGuestSizeFromItemOrSession = (item) => {
    const snap =
      item.sizeSnapshot ??
      item.guestSizeSnapshot ??
      item.sessionSize ??
      null;

    if (snap && typeof snap === 'object') return snap;
    return guestSize;
  };

  const renderSizeLabel = (item) => {
    const fmt = (val) => {
      if (val === null || val === undefined || val === '') return null;
      const num = Number(val);
      return Number.isFinite(num) ? `${num}` : String(val);
    };

    const bodyFromItem = item.bodyMeasurement || item.BodyMeasurement || null;
    const shirtFromItem = item.shirtMeasurement || item.ShirtMeasurement || null;

    const bodyDetails = (m) => {
      if (!m) return null;
      const parts = [
        ['Neck', fmt(m.neckAround ?? m.NeckAround)],
        ['Sleeve', fmt(m.sleeveLength ?? m.SleeveLength)],
        ['Shoulder', fmt(m.shoulderWidth ?? m.ShoulderWidth)],
        ['Chest', fmt(m.chestAround ?? m.ChestAround)],
        ['Waist/Stomach', fmt(m.waistAround ?? m.WaistAround)],
        ['Length', fmt(m.shirtLength ?? m.ShirtLength)],
        ['Biceps', fmt(m.bicepAround ?? m.BicepAround)],
        ['Cuffs/Wrist', fmt(m.wristAround ?? m.WristAround)],
        ['Fit', (m.fit ?? m.Fit ?? '').toString().trim() || null]
      ].filter(([, v]) => v !== null);
      return parts.length ? `Body Measurement: ${parts.map(([k, v]) => `${k} ${v}`).join(', ')}` : 'Body Measurement';
    };

    const shirtDetails = (m) => {
      if (!m) return null;
      const parts = [
        ['Collar/Neck', fmt(m.collarAround ?? m.CollarAround)],
        ['Sleeve', fmt(m.sleeveLength ?? m.SleeveLength)],
        ['Shoulder/Yoke', fmt(m.yokeWidth ?? m.YokeWidth)],
        ['Chest', fmt(m.chestWidth ?? m.ChestWidth)],
        ['Stomach', fmt(m.midsectionWidth ?? m.MidsectionWidth)],
        ['Length', fmt(m.shirtLength ?? m.ShirtLength)],
        ['Biceps', fmt(m.sleeveWidth ?? m.SleeveWidth)],
        ['Cuffs', fmt(m.cuffAround ?? m.CuffAround)]
      ].filter(([, v]) => v !== null);
      return parts.length ? `Shirt Measurement: ${parts.map(([k, v]) => `${k} ${v}`).join(', ')}` : 'Shirt Measurement';
    };

    // logged-in cart fields from server
    if (item.smartSizeName) return `Smart Size: ${item.smartSizeName}`;
    if (item.bodyMeasurementLabel) return bodyDetails(bodyFromItem) || 'Body Measurement';
    if (item.shirtMeasurementLabel) return shirtDetails(shirtFromItem) || 'Shirt Measurement';
    if (item.isMailUsSelected) return 'Mail Us a Shirt';

    const standardCollar = fmt(item.standardCollarSize ?? item.standardCollar);
    const standardSleeve = fmt(item.standardSleeveLength ?? item.standardSleeve);
    const standardFit = (item.standardFit ?? '').toString().trim();
    if (standardCollar || standardSleeve || standardFit) {
      const parts = [];
      if (standardCollar) parts.push(`Collar ${standardCollar}`);
      if (standardSleeve) parts.push(`Sleeve ${standardSleeve}`);
      if (standardFit) parts.push(standardFit);
      return `Standard Size: ${parts.join(', ')}`;
    }

    // guest hints on item
    const itemSizeType =
      item.sizeType ||
      item.selectedSizeType ||
      item.sizeSelectionType ||
      item.size_mode ||
      '';

    const st = String(itemSizeType).toLowerCase();

    if (st.includes('smart')) return 'Smart Size';
    if (st.includes('body')) {
      const snapshot = pickGuestSizeFromItemOrSession(item);
      return bodyDetails(snapshot?.body || snapshot?.bodyMeasurement || bodyFromItem) || 'Body Measurement';
    }
    if (st.includes('shirt')) {
      const snapshot = pickGuestSizeFromItemOrSession(item);
      return shirtDetails(snapshot?.shirt || snapshot?.shirtMeasurement || shirtFromItem) || 'Shirt Measurement';
    }
    if (st.includes('mail')) return 'Mail Us a Shirt';

    // guest snapshot/session
    const gs = pickGuestSizeFromItemOrSession(item);
    if (!gs) return '—';

    const gsType = String(gs.sizeType || '').toLowerCase();

    if (gsType.includes('smart')) return `Smart Size: ${gs.smartSizeName || gs.smartSize || 'Selected'}`;
    if (gsType.includes('body')) return bodyDetails(gs.body || gs.bodyMeasurement || null) || 'Body Measurement';
    if (gsType.includes('shirt')) return shirtDetails(gs.shirt || gs.shirtMeasurement || null) || 'Shirt Measurement';
    if (gsType.includes('mail')) return 'Mail Us a Shirt';

    if (gsType.includes('standard') || !gsType) {
      const s = gs.standard || {};
      const collar = s.collarSize ?? s.collar ?? '';
      const sleeve = s.sleeveLength ?? s.sleeve ?? '';
      const fit = s.fit ?? '';
      const parts = [];
      if (collar) parts.push(`Collar ${collar}`);
      if (sleeve) parts.push(`Sleeve ${sleeve}`);
      if (fit) parts.push(fit);
      return parts.length ? `Standard Size: ${parts.join(', ')}` : 'Standard Size';
    }

    return '—';
  };

  const renderAccessorySelectionLabel = (s) => {
    const left = s?.label || s?.key || 'Option';
    const right = s?.optionLabel || s?.value || '';
    return right ? `${left}: ${right}` : left;
  };

  const moveToCart = async (id) => {
    if (isLoggedIn) {
      try {
        const res = await fetch(`${API_BASE_URL}/UserManagement/AddWishlistItemToCart/${id}`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('AddWishlistItemToCart failed:', res.status, text);
          alert('Failed to move item to cart.');
          return;
        }

        await loadWishlist();
        window.dispatchEvent(new Event('ccc_cart_updated'));
      } catch (err) {
        console.error(err);
        alert('Failed to move item to cart.');
      }
      return;
    }

    // GUEST: remove from wishlist + add to guest cart
    const wl = getGuestWishlist();
    const idx = wl.findIndex((x) => (x.guestWishlistId ?? x.id) === id);
    if (idx === -1) return;

    const item = wl[idx];
    const nextWl = wl.filter((_, i) => i !== idx);

    const cart = getGuestCart();

    cart.push({
      ...item,
      guestCartId: item.guestCartId || (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`),
      // optional: keep a snapshot so each item can show size later even if guest changes size
      sizeSnapshot: item.sizeSnapshot || pickGuestSizeFromItemOrSession(item) || null,
      // remove wishlist-only field
      guestWishlistId: undefined
    });

    setGuestWishlist(nextWl);
    setGuestCart(cart);

    setWishlist(normalizeWishlist(nextWl));
  };

  const deleteWishlistItem = async (id) => {
    if (isLoggedIn) {
      try {
        // Some backends use DELETE, some use POST. Try DELETE first, then fallback.
        let res = await fetch(`${API_BASE_URL}/UserManagement/DeleteWishlistItem/${id}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!res.ok) {
          res = await fetch(`${API_BASE_URL}/UserManagement/DeleteWishlistItem/${id}`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
        }

        if (!res.ok) {
          const text = await res.text();
          console.error('DeleteWishlistItem failed:', res.status, text);
          alert('Failed to delete wishlist item.');
          return;
        }

        await loadWishlist();
        window.dispatchEvent(new Event('ccc_wishlist_updated'));
      } catch (err) {
        console.error(err);
        alert('Failed to delete wishlist item.');
      }
      return;
    }

    const wl = getGuestWishlist();
    const next = wl.filter((x) => (x.guestWishlistId ?? x.id) !== id);
    setGuestWishlist(next);
    setWishlist(normalizeWishlist(next));
  };

  const goToProduct = (item) => {
    const pid = item.productId ?? item.productID ?? item.product_id;
    const fid = item.fabricId ?? item.fabricID ?? item.fabric_id;
    if (!pid) return;
    navigate(fid ? `/product/${pid}?fabricId=${fid}` : `/product/${pid}`);
  };

  return (
    <>
      <Header />
      <main className="rbt-main-wrapper">
        <div className="rbt-breadcrumb-default ptb--100 ptb_md--50 ptb_sm--30 bg-gradient-1">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="breadcrumb-inner text-center">
                  <h2 className="title">My Wishlist</h2>
                  <ul className="page-list">
                    <li className="rbt-breadcrumb-item"><a href="/">Home</a></li>
                    <li><div className="icon-right"><i className="feather-chevron-right"></i></div></li>
                    <li className="rbt-breadcrumb-item active">Wishlist</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rbt-my-account-area rbt-section-gap" style={{ width: '100%' }}>
          <div className="container" style={{ width: '100%' }}>
            <div className="row g-5" style={{ width: '100%' }}>
              <div className="col-lg-9 col-12" style={{ width: '100%' }}>
                <div className="rbt-my-account-inner" style={{ width: '100%' }}>
                  <div className="rbt-my-account-table table-responsive text-center" style={{ width: '100%' }}>
                    <table className="table table-bordered" style={{ width: '100%' }}>
                      <thead className="thead-light">
                        <tr>
                          <th>Image</th>
                          <th>Name</th>
                          <th>Size</th>
                          <th>Monogram</th>
                          <th>Price</th>
                          <th>Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {wishlist.length === 0 ? (
                          <tr>
                            <td colSpan="6">No wishlist items found.</td>
                          </tr>
                        ) : (
                          wishlist.map((item) => (
                            <tr key={item._id}>
                              <td>
                                <img
                                  src={item.fabricImageUrl || "/assets/images/default.jpg"}
                                  alt={item.productName || "Product"}
                                  style={{ width: "60px", height: "60px", objectFit: "cover", cursor: 'pointer' }}
                                  onClick={() => goToProduct(item)}
                                />
                              </td>

                              <td style={{ cursor: 'pointer' }} onClick={() => goToProduct(item)}>
                                <strong>{item.productName}</strong>
                                <br />
                                <small style={{ color: '#666' }}>{item.fabricName}</small>
                                <br />
                                <small style={{ color: '#888', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {(item.styleOptionNames || []).join(', ')}
                                </small>
                                {!!item.accessorySelections?.length && (
                                  <>
                                    <br />
                                    <small style={{ color: '#666', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                      {(item.accessorySelections || []).map(renderAccessorySelectionLabel).join(', ')}
                                    </small>
                                  </>
                                )}
                              </td>

                              <td>{renderSizeLabel(item)}</td>

                              <td>
                                {item.isMonogram ? (
                                  <>
                                    <div>{item.monogramInitials}</div>
                                    <div style={{ fontSize: '0.8em', color: '#888' }}>
                                      {item.monogramStyle}, {item.monogramColor}, {item.monogramPlacement}
                                    </div>
                                  </>
                                ) : (
                                  '—'
                                )}
                              </td>

                              <td>${Number(item.price || 0).toFixed(2)}</td>

                              <td>
                                <div className="d-flex justify-content-center gap-2">
                                  <button
                                    className="rbt-btn btn-gradient btn-sm"
                                    onClick={() => moveToCart(isLoggedIn ? item.wishlistId : (item.guestWishlistId ?? item.id))}
                                  >
                                    Move to Cart
                                  </button>
                                  <button
                                    className="rbt-btn btn-gradient btn-sm"
                                    onClick={() => deleteWishlistItem(isLoggedIn ? item.wishlistId : (item.guestWishlistId ?? item.id))}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {!isLoggedIn && wishlist.length > 0 && (
                    <div className="mt-3" style={{ color: '#666' }}>
                      You’re viewing a guest wishlist (saved in this browser).
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Wishlist;
